import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { KeyTerm, ImportantNote } from "../note-components";
import type { UnitNotes } from "../types";

const biologyUnit3Notes: UnitNotes = {
  unitId: "unit-3",
  unitName: "Unit 3: Cellular Energetics",
  examWeight: "12–16%",
  focus: "Energetics (How living systems use energy and matter to maintain order, grow, and reproduce).",
  sections: [
    {
      id: "topic-3-1",
      title: "Topic 3.1: Enzyme Structure and Function",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧪 Fundamentals of Enzymes</h3>
            <div className="space-y-3">
              <p className="text-slate-700 leading-relaxed">
                Enzymes are biological <strong>catalysts</strong>. They speed up biochemical reactions by lowering the <strong>activation energy</strong> (<InlineMath math="E_a" />) required for the reaction to occur.
              </p>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <h4 className="font-bold text-slate-900">Nature & Stability</h4>
                  <ul className="list-disc pl-5 mt-2 text-sm text-slate-700 space-y-1">
                    <li>Most are proteins (tertiary/quaternary); some are RNA (ribozymes).</li>
                    <li>They are <strong>not consumed</strong> by the reaction; they are reusable.</li>
                  </ul>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <h4 className="font-bold text-slate-900">Thermodynamics</h4>
                  <ul className="list-disc pl-5 mt-2 text-sm text-slate-700 space-y-1">
                    <li>They do <strong>not</strong> change the free energy (<InlineMath math="\Delta G" />).</li>
                    <li>They cannot make an endergonic reaction exergonic.</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧩 Enzyme Structure</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Active Site" 
                definition="A specific region on the enzyme where the substrate binds. It has a unique shape and chemical environment (charge, hydrophobicity) compatible with the substrate."
              />
              <KeyTerm 
                term="Substrate" 
                definition="The specific reactant molecule upon which an enzyme acts."
              />
              <KeyTerm 
                term="Specificity" 
                definition="Enzymes are highly specific. The shape of the active site must match the shape of the substrate (dynamic lock and key)."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚙️ Mechanism of Action</h3>
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-900">Induced Fit Model</h4>
                <p className="text-sm text-slate-700 mt-1">When a substrate enters the active site, the enzyme undergoes a slight conformational change (shape change) to wrap tighter around the substrate, creating the <strong>Enzyme-Substrate Complex</strong>.</p>
              </div>
              
              <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
                <h4 className="font-bold text-slate-800 mb-2">How Enzymes Lower $E_a$:</h4>
                <ul className="list-disc pl-5 space-y-2 text-sm text-slate-700">
                  <li><strong>Template orientation:</strong> Aligns substrates correctly to react.</li>
                  <li><strong>Stress:</strong> Puts physical stress on bonds, making them easier to break.</li>
                  <li><strong>Microenvironment:</strong> Creates a favorable environment (e.g., acidic or nonpolar) for the reaction.</li>
                </ul>
              </div>

              <div className="grid md:grid-cols-2 gap-3">
                <div className="bg-emerald-50 p-3 rounded border border-emerald-200">
                  <h5 className="font-bold text-emerald-900 text-sm">Catabolism</h5>
                  <p className="text-xs text-slate-600">Breaking down complex molecules (hydrolysis).</p>
                </div>
                <div className="bg-amber-50 p-3 rounded border border-amber-200">
                  <h5 className="font-bold text-amber-900 text-sm">Anabolism</h5>
                  <p className="text-xs text-slate-600">Building complex molecules (dehydration synthesis).</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-2",
      title: "Topic 3.2: Environmental Impacts on Enzyme Function",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚠️ Denaturation</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              <strong>Definition:</strong> A structural change in a protein that results in the loss of its biological properties due to the disruption of weak interactions (hydrogen bonds, ionic bonds, disulfide bridges).
            </p>
            <ImportantNote>
              <strong>Reversibility:</strong> Sometimes reversible if the optimal environment is restored; often irreversible if the structural damage to the active site is severe.
            </ImportantNote>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌡️ Environmental Factors</h3>
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-4 rounded-lg border-l-4 border-orange-400">
                <h4 className="font-bold text-orange-900">Temperature</h4>
                <ul className="mt-2 space-y-1 text-sm text-slate-700">
                  <li><strong>Optimum Temp:</strong> Rate is highest (molecular collisions are frequent).</li>
                  <li><strong>Low Temp:</strong> Kinetic energy decreases; collisions are infrequent. Rate slows but enzyme is <strong>not</strong> denatured.</li>
                  <li><strong>High Temp:</strong> Thermal agitation disrupts bonds. The enzyme <strong>denatures</strong> and the rate drops to zero.</li>
                </ul>
              </div>

              <div className="bg-gradient-to-r from-purple-50 to-violet-50 p-4 rounded-lg border-l-4 border-purple-400">
                <h4 className="font-bold text-purple-900">pH</h4>
                <p className="text-sm text-slate-700 mt-1">Outside the <strong>Optimum pH</strong>, excess <InlineMath math="H^+" /> (acidic) or <InlineMath math="OH^-" /> (basic) ions interfere with hydrogen and ionic bonding, leading to denaturation.</p>
              </div>

              <div className="bg-gradient-to-r from-cyan-50 to-blue-50 p-4 rounded-lg border-l-4 border-cyan-400">
                <h4 className="font-bold text-cyan-900">Substrate Concentration</h4>
                <p className="text-sm text-slate-700 mt-1">Increasing concentration increases rate initially. At the <strong>Saturation Point (<InlineMath math="V_{max}" />)</strong>, all active sites are occupied and the rate plateaus.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🚫 Enzyme Inhibitors</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-rose-50 p-4 rounded-lg border border-rose-200">
                <h4 className="font-bold text-rose-900">Competitive Inhibition</h4>
                <ul className="text-xs text-slate-700 mt-2 list-disc pl-4 space-y-1">
                  <li>Inhibitor shape resembles the substrate.</li>
                  <li>Binds to the <strong>active site</strong>, blocking the substrate.</li>
                  <li><strong>Counteraction:</strong> Increase substrate concentration to outcompete the inhibitor.</li>
                </ul>
              </div>
              <div className="bg-pink-50 p-4 rounded-lg border border-pink-200">
                <h4 className="font-bold text-pink-900">Noncompetitive (Allosteric)</h4>
                <ul className="text-xs text-slate-700 mt-2 list-disc pl-4 space-y-1">
                  <li>Inhibitor binds to an <strong>allosteric site</strong> (not the active site).</li>
                  <li>Causes a conformational change, altering the active site shape.</li>
                  <li><strong>Counteraction:</strong> Cannot be overcome by adding more substrate.</li>
                </ul>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-3",
      title: "Topic 3.3: Cellular Energy",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📜 Laws of Thermodynamics</h3>
            <div className="space-y-3">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900 text-sm">1. First Law (Conservation of Energy)</h4>
                <p className="text-xs text-slate-700 mt-1">Energy cannot be created or destroyed, only transformed. Organisms must obtain energy from the environment to transform it into usable ATP.</p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-900 text-sm">2. Second Law (Entropy)</h4>
                <p className="text-xs text-slate-700 mt-1">Every transfer increases entropy (disorder). Organisms require constant energy input to maintain order; loss of energy flow results in death.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">⚡ Metabolic Pathways & Energy Coupling</h3>
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-green-50 border border-green-200">
                  <h4 className="font-bold text-green-900 text-sm">Exergonic Reactions</h4>
                  <p className="text-xs text-slate-700">Release energy; spontaneous; <InlineMath math="-\Delta G" />. (e.g., Cellular Respiration).</p>
                </div>
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                  <h4 className="font-bold text-blue-900 text-sm">Endergonic Reactions</h4>
                  <p className="text-xs text-slate-700">Absorb energy; non-spontaneous; <InlineMath math="+\Delta G" />. (e.g., Photosynthesis).</p>
                </div>
              </div>
              <KeyTerm 
                term="Energy Coupling" 
                definition="The use of an exergonic process to drive an endergonic one. Primarily managed by ATP."
              />
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-900">ATP (Adenosine Triphosphate)</h4>
                <p className="text-sm text-slate-700 mt-1">The 'energy currency.' Breaking the bond between the 2nd and 3rd phosphate group releases energy used for cellular work.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🛤️ Sequential Pathways</h3>
            <p className="text-slate-700 leading-relaxed text-sm">
              Metabolic pathways are a series of enzymatic steps where the product of one reaction becomes the reactant for the next. This allows for controlled, efficient energy transfer.
            </p>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-4",
      title: "Topic 3.4: Photosynthesis",
      content: (
        <div className="space-y-6">
          <div className="bg-emerald-900 text-white p-6 rounded-xl shadow-lg">
            <h4 className="text-center font-mono font-bold text-lg mb-2">General Equation</h4>
            <BlockMath math="6CO_2 + 6H_2O + \text{Light Energy} \rightarrow C_6H_{12}O_6 + 6O_2" />
          </div>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🦠 Origins & Structure</h3>
            <ul className="list-disc pl-5 space-y-2 text-sm text-slate-700">
              <li><strong>Evolutionary Origins:</strong> First evolved in prokaryotes (cyanobacteria). Oxygenated the atmosphere and led to eukaryotic photosynthesis via <strong>endosymbiosis</strong>.</li>
              <li><strong>Chloroplast Structure:</strong>
                <ul className="list-circle pl-5 mt-1">
                  <li><strong>Thylakoids:</strong> Membrane sacs containing chlorophyll; site of Light Dependent Reactions.</li>
                  <li><strong>Stroma:</strong> Fluid-filled space; site of the Calvin Cycle.</li>
                </ul>
              </li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌊 Stage 1: Light Dependent Reactions</h3>
            <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
              <div className="grid grid-cols-3 gap-2 mb-4 text-center text-xs">
                <div className="p-2 bg-white rounded border"><strong>Inputs:</strong> H₂O, Light, ADP, NADP⁺</div>
                <div className="p-2 bg-emerald-100 rounded border"><strong>Location:</strong> Thylakoid</div>
                <div className="p-2 bg-white rounded border"><strong>Outputs:</strong> O₂, ATP, NADPH</div>
              </div>
              <ol className="list-decimal pl-5 space-y-2 text-sm text-slate-700">
                <li><strong>Photosystems (PSII and PSI):</strong> Chlorophyll absorbs light, exciting electrons.</li>
                <li><strong>Photolysis:</strong> Water is split at PSII to replace electrons, releasing <InlineMath math="O_2" />.</li>
                <li><strong>ETC:</strong> Energy from electrons pumps protons into the thylakoid lumen, creating a <strong>proton gradient</strong>.</li>
                <li><strong>Chemiosmosis:</strong> Protons flow back through <strong>ATP Synthase</strong> (Photophosphorylation).</li>
                <li><strong>NADPH Formation:</strong> Electrons reduce <InlineMath math="NADP^+" /> to NADPH.</li>
              </ol>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🍃 Stage 2: The Calvin Cycle</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <div className="grid grid-cols-3 gap-2 mb-4 text-center text-xs">
                <div className="p-2 bg-white rounded border"><strong>Inputs:</strong> CO₂, ATP, NADPH</div>
                <div className="p-2 bg-green-100 rounded border"><strong>Location:</strong> Stroma</div>
                <div className="p-2 bg-white rounded border"><strong>Outputs:</strong> G3P, ADP, NADP⁺</div>
              </div>
              <ol className="list-decimal pl-5 space-y-2 text-sm text-slate-700">
                <li><strong>Carbon Fixation:</strong> <strong>Rubisco</strong> attaches <InlineMath math="CO_2" /> to RuBP.</li>
                <li><strong>Reduction:</strong> ATP and NADPH provide energy to convert the molecule into <strong>G3P</strong> (sugar).</li>
                <li><strong>Regeneration:</strong> Some G3P leaves to make glucose; the rest recycles to RuBP.</li>
              </ol>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-3-5",
      title: "Topic 3.5: Cellular Respiration",
      content: (
        <div className="space-y-6">
          <div className="bg-slate-800 text-white p-6 rounded-xl shadow-lg">
            <h4 className="text-center font-mono font-bold text-lg mb-2">General Equation</h4>
            <BlockMath math="C_6H_{12}O_6 + 6O_2 \rightarrow 6CO_2 + 6H_2O + \text{ATP}" />
          </div>

          <section className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
              <h4 className="font-bold text-blue-900">1. Glycolysis (Evolutionarily Conserved)</h4>
              <p className="text-sm text-slate-700"><strong>Location:</strong> Cytoplasm. <strong>Yield:</strong> 2 Net ATP, 2 NADH. Anaerobic process; evidence of common ancestry as it occurs in all organisms.</p>
            </div>

            <div className="bg-indigo-50 p-4 rounded-lg border-l-4 border-indigo-400">
              <h4 className="font-bold text-indigo-900">2. Pyruvate Oxidation (Link Reaction)</h4>
              <p className="text-sm text-slate-700"><strong>Location:</strong> Mitochondrial Matrix. Pyruvate oxidized to Acetyl-CoA. <strong>Yield:</strong> <InlineMath math="CO_2" /> released, NADH produced.</p>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400">
              <h4 className="font-bold text-purple-900">3. The Krebs Cycle (Citric Acid Cycle)</h4>
              <p className="text-sm text-slate-700"><strong>Location:</strong> Matrix. Acetyl-CoA is broken down. <strong>Yield per glucose:</strong> 2 ATP, 6 NADH, 2 <InlineMath math="FADH_2" />, 4 <InlineMath math="CO_2" />. Loads electron carriers for the ETC.</p>
            </div>

            <div className="bg-cyan-50 p-4 rounded-lg border-l-4 border-cyan-400">
              <h4 className="font-bold text-cyan-900">4. Oxidative Phosphorylation</h4>
              <p className="text-xs font-bold text-slate-600 mb-2">Location: Inner Mitochondrial Membrane (Cristae)</p>
              <ul className="list-disc pl-5 space-y-2 text-sm text-slate-700">
                <li><strong>ETC:</strong> NADH and <InlineMath math="FADH_2" /> drop off electrons. Energy used to pump <InlineMath math="H^+" /> into <strong>intermembrane space</strong>.</li>
                <li><strong>Oxygen:</strong> The <strong>final electron acceptor</strong>; forms water. Without <InlineMath math="O_2" />, ATP production stops.</li>
                <li><strong>Chemiosmosis:</strong> <InlineMath math="H^+" /> flows through <strong>ATP Synthase</strong> to make ~30-34 ATP.</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🧪 Anaerobic & Specialized Processes</h3>
            <div className="grid md:grid-cols-2 gap-3 mb-4">
              <div className="bg-rose-50 p-4 rounded-lg border border-rose-200">
                <h4 className="font-bold text-rose-900">Fermentation</h4>
                <p className="text-xs text-slate-700 mt-1">Regenerates <InlineMath math="NAD^+" /> so glycolysis continues. <strong>Alcohol:</strong> Ethanol + <InlineMath math="CO_2" />. <strong>Lactic Acid:</strong> Lactate.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-900">Thermogenesis (Decoupling)</h4>
                <p className="text-xs text-slate-700 mt-1">Protons leak back across the membrane without generating ATP; energy is released as <strong>heat</strong> for temperature regulation.</p>
              </div>
            </div>
          </section>

          <section className="bg-slate-100 p-5 rounded-lg border border-slate-300">
            <h3 className="text-lg font-bold text-slate-800 mb-3">🔗 Critical Connections & Exam Tips</h3>
            <ul className="space-y-3 text-sm text-slate-700">
              <li className="flex gap-2">
                <span className="text-blue-600 font-bold">1.</span>
                <p><strong>Photosynthesis vs. Respiration:</strong> One stores energy, the other releases it. <strong>Plants perform both.</strong></p>
              </li>
              <li className="flex gap-2">
                <span className="text-blue-600 font-bold">2.</span>
                <p><strong>The Gradients:</strong> Photosynthesis has high <InlineMath math="H^+" /> in the Thylakoid Lumen; Respiration has high <InlineMath math="H^+" /> in the Intermembrane Space.</p>
              </li>
              <li className="flex gap-2">
                <span className="text-blue-600 font-bold">3.</span>
                <p><strong>Evolution:</strong> Glycolysis is proof of common ancestry. Variation in enzyme/metabolic efficiency impacts <strong>fitness</strong>.</p>
              </li>
              <li className="flex gap-2">
                <span className="text-blue-600 font-bold">4.</span>
                <p><strong>Skills:</strong> Calculate rates from slopes on graphs and use error bars to find statistical significance.</p>
              </li>
            </ul>
          </section>
        </div>
      )
    }
  ]
};

export default biologyUnit3Notes;